/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { toJS } from 'mobx';
import { types } from 'mobx-state-tree';
import { getBulkExportStatusByBulkId } from '../services/bulkExportService';
import {
  bulkActionFieldNames,
  EXPORT_STATUS
} from '../constants/bulkActionStatusConstants';
import { toExportProjectListTitleCase } from '../utils/bulkActionUtils';

// Helper function to determine status from a bulk request
function determineStatusFromRequest(request) {
  // Convert status to Title Case
  const rawStatus = request?.status || EXPORT_STATUS.QUEUED;
  return toExportProjectListTitleCase(rawStatus);
}

function getCompletedCountFromRequest(statusCounts) {
  if (!Array.isArray(statusCounts)) return 0;
  // Get completed and error counts separately
  const completedCount =
    statusCounts.find(
      s => s.status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    )?.count || 0;
  const errorCount =
    statusCounts.find(
      s => s.status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    )?.count || 0;

  // Return the sum of completed and error counts
  return completedCount + errorCount;
}

function getProgressFromRequest(statusCounts, totalCount) {
  const completedCount =
    statusCounts?.find(
      s => s?.status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    )?.count || 0;
  const falilureCount =
    statusCounts?.find(
      s => s?.status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    )?.count || 0;
  const total = completedCount + falilureCount;
  return (total / totalCount) * 100;
}

const BulkExport = types.model('BulkExport', {
  id: types.optional(types.identifier, () =>
    Math.random().toString(36).substring(2, 9)
  ),
  operationType: types.optional(types.string, 'PDF'),
  title: types.maybeNull(types.string),
  userid: types.maybeNull(types.string),
  exportInitiateTime: types.maybeNull(types.string),
  startTime: types.maybeNull(types.string),
  endTime: types.maybeNull(types.string),
  errorDetails: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  bulkRequestId: types.maybeNull(types.string),
  fileLocation: types.maybeNull(types.string)
});

// Define the main status tracking model
export default types
  .model('BulkExportProjectList', {
    exportProjectData: types.optional(types.array(BulkExport), []),
    hasMore: types.maybeNull(types.boolean, false),
    totalCount: types.maybeNull(types.number, 0),
    sortBy: types.optional(types.string, bulkActionFieldNames.START_TIME),
    sortDirection: types.optional(types.enumeration(['asc', 'desc']), 'desc'),
    page: types.optional(types.number, 1),
    pageSize: types.optional(types.number, 100),
    isLoading: types.optional(types.boolean, false),
    total: types.optional(types.number, 10),
    completed: types.optional(types.number, 10),
    progress: types.optional(types.number, 0),
    statusCounts: types.optional(
      types.array(
        types.model({
          status: types.string,
          count: types.number
        })
      ),
      []
    )
  })
  .views(self => ({
    get sortedExportProjectData() {
      //   const sortableData = [...self.exportProjectData];
      const sortableData = toJS(self.exportProjectData);
      if (self.sortBy) {
        sortableData.sort((a, b) => {
          if (a[self.sortBy] < b[self.sortBy]) {
            return self.sortDirection === 'asc' ? -1 : 1;
          }
          if (a[self.sortBy] > b[self.sortBy]) {
            return self.sortDirection === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }

      return sortableData;
    }
  }))
  .actions(self => ({
    resetBulkPageIndex() {
      self.page = 1;
    },

    resetList() {
      self.bulkPublishReqIds = [];
      self.hasMore = false;
      self.totalCount = 0;
    },
    setBulkProjectList(bulkRequests) {
      self.exportProjectData = bulkRequests;
    },
    setHasMore(hasMore) {
      self.hasMore = hasMore;
    },
    setTotalCount(totalCount) {
      self.totalCount = totalCount;
    },
    setNewPageValue(page) {
      self.page = page;
    },
    setStatusCounts(statusCounts) {
      self.statusCounts = statusCounts;
    },

    setCompletedCount(statusCounts) {
      // Sets the sum of completed and error counts as "completed" for progress tracking
      self.completed = getCompletedCountFromRequest(statusCounts);
    },

    setSorting(field) {
      if (self.sortBy === field) {
        // Toggle direction if same field
        self.sortDirection = self.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New field, default to ascending
        self.sortBy = field;
        self.sortDirection = 'asc';
      }
    },

    setIsLoading(isLoading) {
      self.isLoading = isLoading;
    },

    setProgress(progress) {
      self.progress = progress;
    },

    async loadBulkProjects(bulkRequestId, resetProjects) {
      const apiParams = {
        bulkRequestId,
        page: 1,
        pageSize: self.pageSize
      };

      if (resetProjects) {
        self.resetBulkPageIndex();
      }
      self.setNewPageValue(1);
      self.setIsLoading(true);

      const exportStatusData = await getBulkExportStatusByBulkId(apiParams);

      const {
        records = [],
        hasMore = false,
        totalCount = 0,
        statusCounts = []
      } = exportStatusData;
      const newItems = [];
      records?.forEach(request => {
        // Define default values for fields
        const status = determineStatusFromRequest(request);
        newItems.push({
          id: Math.random().toString(36).substring(2, 9),
          operationType: request.operationType || 'PDF',
          title: request.title || '',
          fileLocation: request.fileLocation || '',
          userid: request.userid || '',
          exportInitiateTime: request.exportInitiateTime || '',
          startTime: request.startTime || '',
          endTime: request.endTime || '',
          errorDetails: request.errorMessage || '',
          status,
          bulkRequestId: request.bulkRequestId
        });
      });
      self.setBulkProjectList(newItems);
      self.setHasMore(hasMore);
      self.setTotalCount(totalCount);
      self.setStatusCounts(statusCounts);
      self.setCompletedCount(statusCounts);
      self.setIsLoading(false);
      const progress = getProgressFromRequest(statusCounts, totalCount);
      self.setProgress(progress);
    },

    async updateBulkExportProjectList(bulkRequestId) {
      const updatedPageValue = self.page + 1;
      self.setNewPageValue(updatedPageValue);
      const apiParams = {
        bulkRequestId,
        page: updatedPageValue || self.page,
        pageSize: self.pageSize
      };
      self.setIsLoading(true);
      const exportStatusData = await getBulkExportStatusByBulkId(apiParams);
      const {
        records = [],
        hasMore = false,
        totalCount = 0,
        statusCounts = []
      } = exportStatusData;
      const newItems = [];
      records.forEach(request => {
        const status = determineStatusFromRequest(request);
        newItems.push({
          id: Math.random().toString(36).substring(2, 9),
          operationType: 'Publish',
          title: request.title || '',
          fileLocation: request.fileLocation || '',
          userid: request.userid || '',
          exportInitiateTime: request.exportInitiateTime || '',
          startTime: request.startTime || '',
          endTime: request.endTime || '',
          errorDetails: request.errorMessage || '',
          status,
          // Sum of completed and error counts
          bulkRequestId: request.bulkRequestId
        });
      });
      const currentBulkProjectList = [
        ...toJS(self.exportProjectData),
        ...newItems
      ];
      self.setBulkProjectList(currentBulkProjectList);
      self.setHasMore(hasMore);
      self.setTotalCount(totalCount);
      self.setStatusCounts(statusCounts);
      self.setCompletedCount(statusCounts);
      self.setIsLoading(false);
    }
  }));
